import { classnames, keepOnlyProperty } from '@aibees/shared/utils';
import { Box, Typography } from '@mui/material';
import { memo, useMemo } from 'react';
import { useFormContext } from 'react-hook-form';
import { useBulkFilterItemState } from '../../hooks/use-bulk-filter-item-state';
import * as SX from './sx';
import type { BulkFilterInputErrorProps as Props } from './types';

export const BulkFilterInputError = memo(
  ({ className, item, ...filter }: Props) => {
    const methods = useFormContext();
    const allValues = methods.getValues();

    const values = keepOnlyProperty(allValues, item.field || '');
    const validationResult =
      filter.metadata?.validation?.patternValidation?.(allValues);
    const invalidItems = validationResult?.[item.field!].invalids;
    const duplicates = validationResult?.[item.field!].duplicates;
    const { count, limit } = useBulkFilterItemState(
      item.field!,
      item.metadata,
      values
    );
    const limitReached = limit && count > limit;

    const errorMessages = useMemo(() => {
      const invalidItemsLength = invalidItems?.length ?? 0;
      const invalidItemsEntries = invalidItemsLength > 1 ? 'entries' : 'entry';

      if (invalidItems?.length && !duplicates?.length) {
        return `${invalidItems?.length} ${invalidItemsEntries} could not be recognized out of ${count}.`;
      }

      const duplicatesLength = duplicates?.length ?? 0;
      const duplicatesEntries = duplicatesLength > 1 ? 'entries' : 'entry';

      if (!invalidItems?.length && duplicates?.length) {
        return `${duplicates?.length} Duplicated ${duplicatesEntries} were found out of ${count}.`;
      }
      return `${invalidItems?.length} ${invalidItemsEntries} couldn’t be recognized and ${duplicates?.length} ${duplicatesEntries} Duplicates out of ${count}.`;
    }, [invalidItems, duplicates, count]);

    if (
      !Object.values(values).some(Boolean) ||
      (!invalidItems?.length && !duplicates?.length)
    )
      return null;

    return (
      <Box
        className={classnames('BulkFilterInputError', className)}
        paddingTop={0.5}
        sx={SX.root}
      >
        <Typography variant="body4" color="error.500">
          {errorMessages}
        </Typography>
      </Box>
    );
  }
);

BulkFilterInputError.displayName = 'BulkFilterInputError';
